////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/**
 * ════════════════════════════════════════════════════════════════════════════════
 * PAYMENT POD - SUBSCRIPTION MANAGEMENT ORCHESTRATOR (POD/PEA PATTERN)
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * ARCHITECTURAL ROLE: Orchestrates pricing tier selection, billing cycle toggle,
 * Stripe checkout integration, and subscription creation flow
 *
 * I[R{DVT}]O PATTERN:
 *   INPUT: Tier selection (tier_one, tier_two, tier_three, tier_four), billing cycle preference
 *   REQUIREMENTS: Pricing display, Stripe integration, authentication check, sales contact
 *   OUTPUT: Stripe checkout URL or Account dashboard navigation, subscription created
 *
 * PAYMENT FLOW: Customer → Stripe hosted checkout → webhook → Account activation
 * PRICING: Credit-based @ $25/million credits - Monthly or annual (2 months free)
 *
 * T_TIER_RENAME_2026_01_02: Updated tier names from starter/professional/team/enterprise
 * to tier_one/tier_two/tier_three/tier_four to avoid confusion with team functionality
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Pea_Lobby_Payment_SRC_Auth_Customer_Kings from '../pea/Bitsbound_Kings_Customer_Auth_SRC_Payment_Lobby_Pea.js';

// Google Analytics/Ads Tracking - TypeScript declarations for global tracking functions
declare global {
  interface Window {
    bbTrackEvent?: (eventName: string, params?: Record<string, unknown>) => void;
    bbTrackConversion?: (conversionLabel: string, value?: number, currency?: string) => void;
    bbEvents?: {
      signupStart: () => void;
      signupComplete: () => void;
      contractUpload: () => void;
      analysisStart: () => void;
      analysisComplete: () => void;
      pricingView: (plan: string) => void;
      checkoutStart: (plan: string, value: number) => void;
      purchase: (plan: string, value: number) => void;
      demoRequest: () => void;
      trialStart: () => void;
    };
  }
}

interface PricingTier {
  name: string;
  displayName: string;
  price: { monthly: number; annual: number };
  features: string[];
  limits: {
    apiCalls: number;
    arbiters: string[];
    support: string;
  };
  recommended?: boolean;
}

const Pod_Lobby_Payment_SRC_Auth_Customer_Kings: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState<string>('tier_two');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [isLoading, setIsLoading] = useState(false);

  // T_TIER_RENAME_2026_01_02: Credit-based pricing tiers
  // T_PRICE_UPDATE_2026_01_03: All tiers @ $30 per million credits (flat rate)
  const pricingTiers: PricingTier[] = [
    {
      name: 'tier_one',
      displayName: 'Tier One',
      price: { monthly: 30, annual: 300 }, // 2 months free on annual
      features: [
        '1 million credits per month',
        'All 9 contract verticals',
        'Real OOXML Track Changes',
        'Negotiation email drafts',
        'Risk analysis dashboard',
        'Email support'
      ],
      limits: {
        apiCalls: 1000000, // 1M credits
        arbiters: ['All 9 verticals'],
        support: 'Email'
      }
    },
    {
      name: 'tier_two',
      displayName: 'Tier Two',
      price: { monthly: 150, annual: 1500 }, // 2 months free on annual
      features: [
        '5 million credits per month',
        'All 9 contract verticals',
        'Real OOXML Track Changes',
        'Negotiation email drafts',
        'Risk analysis dashboard',
        'Priority email support',
        'Playbook customization'
      ],
      limits: {
        apiCalls: 5000000, // 5M credits
        arbiters: ['All 9 verticals'],
        support: 'Priority'
      },
      recommended: true
    },
    {
      name: 'tier_three',
      displayName: 'Tier Three',
      price: { monthly: 750, annual: 7500 }, // 2 months free on annual
      features: [
        '25 million credits per month',
        'All 9 contract verticals',
        'Real OOXML Track Changes',
        'Negotiation email drafts',
        'Risk analysis dashboard',
        'Priority support with SLA',
        'Playbook customization'
      ],
      limits: {
        apiCalls: 25000000, // 25M credits
        arbiters: ['All 9 verticals'],
        support: 'Priority SLA'
      }
    },
    {
      name: 'tier_four',
      displayName: 'Tier Four',
      price: { monthly: 4500, annual: 45000 }, // 2 months free on annual
      features: [
        '150 million credits per month',
        'All 9 contract verticals',
        'Real OOXML Track Changes',
        'Negotiation email drafts',
        'Risk analysis dashboard',
        'Dedicated account manager',
        'Custom playbook development',
        'SSO & advanced security',
        'API access',
        'Custom integrations'
      ],
      limits: {
        apiCalls: 150000000, // 150M credits
        arbiters: ['All 9 verticals + Custom'],
        support: 'Dedicated'
      }
    }
  ];
  
  const handleSelectTier = (tierName: string) => {
    setSelectedTier(tierName);
    // Track tier selection in Google Analytics
    if (window.bbEvents?.pricingView) {
      window.bbEvents.pricingView(tierName);
    }
  };
  
  const handleToggleBilling = () => {
    setBillingCycle(prev => prev === 'monthly' ? 'annual' : 'monthly');
  };
  
  const handleSubscribe = async () => {
    if (!selectedTier) return;

    // Track checkout initiation in Google Analytics/Ads
    const selectedPricing = pricingTiers.find(t => t.name === selectedTier);
    const priceValue = selectedPricing?.price[billingCycle] || 0;
    if (window.bbEvents?.checkoutStart) {
      window.bbEvents.checkoutStart(selectedTier, priceValue);
    }

    setIsLoading(true);

    try {
      // Get auth token
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        // Redirect to sign in if not authenticated
        navigate('/auth/sign-in');
        return;
      }
      
      // TRANSFORMATION: Environment-aware auth backend URL
      // VALIDATION: Check for production hostnames (main domain OR direct Vercel app access)
      const hostname = window.location.hostname;
      const isProduction = hostname === 'www.bitsbound.com' ||
                          hostname === 'bitsbound.com' ||
                          hostname.includes('vercel.app');
      const authBackendUrl = isProduction
        ? 'https://bitsbound-auth-backend.vercel.app'  // DEFINITION: Production auth backend
        : 'http://localhost:2102';                      // DEFINITION: Local development (port 2102)
      const response = await fetch(`${authBackendUrl}/api/auth/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          tier: selectedTier,
          billingCycle: billingCycle
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Redirect to Stripe checkout or account dashboard
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          navigate('/account');
        }
      } else {
        console.error('Subscription failed:', data.error);
      }
    } catch (error) {
      console.error('Subscription error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleContactSales = () => {
    // Track Tier Four inquiry in Google Analytics/Ads
    if (window.bbEvents?.demoRequest) {
      window.bbEvents.demoRequest();
    }
    // Open contact form or redirect to sales page
    // T_TIER_RENAME_2026_01_02: Updated from Enterprise to Tier Four
    window.location.href = 'mailto:admin@bitsbound.com?subject=BitsBound%20Tier%20Four%20Inquiry';
  };
  
  return (
    <Pea_Lobby_Payment_SRC_Auth_Customer_Kings
      pricingTiers={pricingTiers}
      selectedTier={selectedTier}
      billingCycle={billingCycle}
      isLoading={isLoading}
      onSelectTier={handleSelectTier}
      onToggleBilling={handleToggleBilling}
      onSubscribe={handleSubscribe}
      onContactSales={handleContactSales}
    />
  );
};

export default Pod_Lobby_Payment_SRC_Auth_Customer_Kings;